import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Calendar, ArrowLeft, Loader2, Tag, Twitter, Facebook, Linkedin, Link2, Check } from "lucide-react";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";

type Post = {
  id: string; title: string; slug: string; excerpt: string; content: string;
  cover_image_url: string; category: string;
  published_at: string | null; created_at: string;
};

const CATEGORY_STYLES: Record<string, string> = {
  "General":       "bg-gray-700/70 text-gray-200 border-gray-600/40",
  "News":          "bg-blue-900/60 text-blue-300 border-blue-700/40",
  "Achievements":  "bg-amber-900/60 text-amber-300 border-amber-700/40",
  "Insights":      "bg-purple-900/60 text-purple-300 border-purple-700/40",
  "Events":        "bg-pink-900/60 text-pink-300 border-pink-700/40",
  "Academy":       "bg-emerald-900/60 text-emerald-300 border-emerald-700/40",
  "Transfer News": "bg-orange-900/60 text-orange-300 border-orange-700/40",
  "Scouting":      "bg-cyan-900/60 text-cyan-300 border-cyan-700/40",
};

function CategoryBadge({ category }: { category: string }) {
  const cls = CATEGORY_STYLES[category] ?? CATEGORY_STYLES["General"];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider border ${cls}`}>
      <Tag size={10} />{category}
    </span>
  );
}

function ShareButtons({ title, url }: { title: string, url: string }) {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, '_blank')} className="p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-white transition-colors">
        <Twitter size={16} />
      </button>
      <button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')} className="p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-white transition-colors">
        <Facebook size={16} />
      </button>
      <button onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank')} className="p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-white transition-colors">
        <Linkedin size={16} />
      </button>
      <button onClick={copyToClipboard} className="p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-white transition-colors">
        {copied ? <Check size={16} className="text-emerald-400" /> : <Link2 size={16} />}
      </button>
    </div>
  );
}

export default function BlogPost() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug;

  const { data: post, isLoading, error } = useQuery<Post>({
    queryKey: ["/api/content", "/api/blog", slug],
    queryFn: async () => {
      if (!slug) throw new Error("Slug is required");
      try {
        const q = query(
          collection(db, "blog_posts"),
          where("slug", "==", slug),
          where("status", "==", "published"),
          limit(1)
        );
        const snap = await getDocs(q);
        if (snap.empty) throw new Error("Blog post not found");
        const docSnap = snap.docs[0];
        const item = docSnap.data();
        return {
          id: docSnap.id,
          title: item.title || "",
          slug: item.slug || "",
          excerpt: item.excerpt || "",
          content: item.content || "",
          cover_image_url: item.cover_image_url || "",
          category: item.category || "General",
          published_at: item.published_at || "",
          created_at: item.created_at || "",
        };
      } catch (err: any) {
        console.error("Failed to fetch blog post from Firestore:", err);
        throw err;
      }
    },
    enabled: !!slug,
    retry: false,
  });

  if (isLoading) return (
    <PageTransition className="pt-32 flex justify-center">
      <Loader2 className="animate-spin text-primary" size={32} />
    </PageTransition>
  );

  if (error || !post) return (
    <PageTransition className="pt-32 pb-24 text-center">
      <h1 className="text-3xl font-display font-bold text-white mb-4">Post Not Found</h1>
      <Link href="/blog" className="text-primary font-semibold">← Back to Blog</Link>
    </PageTransition>
  );

  const postUrl = typeof window !== "undefined"
    ? `${window.location.origin}/blog/${post.slug}`
    : `/blog/${post.slug}`;

  const publishedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const category = post.category ?? "General";

  return (
    <>
      <SeoHead
        title={post.title}
        description={post.excerpt || `Read ${post.title} on the Eliada Sports Agency blog.`}
        path={`/blog/${post.slug}`}
      />
      <PageTransition className="pt-24 pb-24">

        {/* Cover image hero */}
        {post.cover_image_url ? (
          <div className="relative h-72 md:h-[28rem] overflow-hidden">
            <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
        ) : (
          <div className="h-24" />
        )}

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">

          {/* Back + share row */}
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <Link href="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary font-medium transition-colors">
              <ArrowLeft size={14} /> Back to Blog
            </Link>
            <ShareButtons title={post.title} url={postUrl} />
          </div>

          {/* Meta row: category + date */}
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <CategoryBadge category={category} />
            {publishedDate && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar size={12} className="text-primary" />
                {publishedDate}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-5xl font-display font-bold text-white leading-tight">{post.title}</h1>

          {/* Excerpt as pull quote */}
          {post.excerpt && (
            <div className="mt-6 border-l-2 border-primary pl-5">
              <p className="text-lg text-gray-300 leading-relaxed italic">{post.excerpt}</p>
            </div>
          )}

          {/* Divider */}
          <div className="mt-10 mb-8 border-t border-white/10" />

          {/* Article body */}
          <div className="text-gray-300 leading-[1.85] text-[1.05rem] space-y-6 whitespace-pre-wrap">
            {post.content}
          </div>

          {/* Bottom share strip */}
          <div className="mt-14 pt-8 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
            <Link href="/blog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary font-medium transition-colors">
              <ArrowLeft size={14} /> All Articles
            </Link>
            <ShareButtons title={post.title} url={postUrl} />
          </div>

        </div>
      </PageTransition>
    </>
  );
}
